/**
 * topmeeps · Logo components
 *
 * The mark is "Gaussians" — three filled normal-distribution curves on a
 * shared baseline (y=36 in a 48×40 viewBox), drawn left → center → right
 * so the tallest/narrowest right curve sits on top. The progression
 * (wider-and-shorter on the left, narrower-and-taller on the right) is
 * the brand — don't change the curve order or add a fourth.
 */

import type { CSSProperties, SVGProps } from 'react'

const COLORS = {
  paper: '#F2EBDA',
  ink: '#261B12',
  terra: '#C26345',
  felt: '#3F6E4E',
  gilt: '#B98427',
  /** Lighter terra tint — only acceptable terra substitute on dark surfaces (wordmark only) */
  terraLight: '#E58A6E',
  /** Gilt background tint — only acceptable accent on terra surface (wordmark only) */
  giltLight: '#F5E2B3',
} as const

export type LogoSurface = 'paper' | 'felt' | 'ink' | 'terra'

interface MarkRecipe {
  left: string
  center: string
  right: string
}

const SURFACE_RECIPES: Record<LogoSurface, MarkRecipe> = {
  paper: { left: COLORS.felt, center: COLORS.gilt, right: COLORS.terra },
  // On felt or ink, the left (felt-green) curve would disappear into the
  // background — swap it to paper. Center and right stay as-is.
  felt: { left: COLORS.paper, center: COLORS.gilt, right: COLORS.terra },
  ink: { left: COLORS.paper, center: COLORS.gilt, right: COLORS.terra },
  // On terra, all three curves go paper-colored to read against the ground.
  terra: { left: COLORS.paper, center: COLORS.paper, right: COLORS.paper },
}

// Curve parameters from README.md §"Geometry".
const GAUSS_PARAMS = {
  left: { cx: 10, sigma: 8.0, h: 14 },
  center: { cx: 24, sigma: 5.5, h: 23 },
  right: { cx: 38, sigma: 3.5, h: 33 },
} as const

const BASELINE = 36
const X_MIN = 0
const X_MAX = 48

function gaussPath(cx: number, sigma: number, h: number): string {
  const step = 0.5
  const twoSigmaSq = 2 * sigma * sigma
  let d = `M ${X_MIN} ${BASELINE}`
  for (let x = X_MIN; x <= X_MAX + 1e-6; x += step) {
    const y = BASELINE - h * Math.exp(-((x - cx) ** 2) / twoSigmaSq)
    d += ` L ${+x.toFixed(2)} ${+y.toFixed(2)}`
  }
  d += ` L ${X_MAX} ${BASELINE} Z`
  return d
}

// Precompute paths once — they're constants.
const PATHS = {
  left: gaussPath(GAUSS_PARAMS.left.cx, GAUSS_PARAMS.left.sigma, GAUSS_PARAMS.left.h),
  center: gaussPath(GAUSS_PARAMS.center.cx, GAUSS_PARAMS.center.sigma, GAUSS_PARAMS.center.h),
  right: gaussPath(GAUSS_PARAMS.right.cx, GAUSS_PARAMS.right.sigma, GAUSS_PARAMS.right.h),
} as const

// ── LogoMark ────────────────────────────────────────────────────

export interface LogoMarkProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Rendered height in pixels. Mark renders at 1.2× this in width (viewBox is 48:40). Default: 28 */
  size?: number
  /** Surface the mark sits on; selects color recipe per BRAND. Default: 'paper' */
  surface?: LogoSurface
  /** Force monochrome — all three fills inherit `color` (currentColor). For stamps, foil, single-color print. */
  monochrome?: boolean
  /** Manual color override; replaces the surface recipe entirely. */
  colors?: Partial<MarkRecipe>
  /** Accessible label. Empty string marks decorative. Default: 'topmeeps' */
  title?: string
}

/**
 * The topmeeps mark — three gaussian curves on a shared baseline.
 * Left = widest/shortest (felt), center = middle (gilt), right = tallest/narrowest (terra).
 */
export function LogoMark({
  size = 28,
  surface = 'paper',
  monochrome = false,
  colors,
  title = 'topmeeps',
  style,
  ...rest
}: LogoMarkProps) {
  const recipe: MarkRecipe = monochrome
    ? { left: 'currentColor', center: 'currentColor', right: 'currentColor' }
    : { ...SURFACE_RECIPES[surface], ...colors }

  const isDecorative = title === ''
  const a11yProps = isDecorative
    ? { 'aria-hidden': true, role: 'presentation' as const }
    : { role: 'img' as const, 'aria-label': title }

  // ViewBox is 48 wide × 40 tall (1.2:1). Width derived so the consumer-set
  // `size` controls the rendered height.
  const width = Math.round((size * 48) / 40)
  const mergedStyle: CSSProperties = { flexShrink: 0, ...style }

  return (
    <svg
      width={width}
      height={size}
      viewBox="0 0 48 40"
      xmlns="http://www.w3.org/2000/svg"
      style={mergedStyle}
      {...a11yProps}
      {...rest}
    >
      {!isDecorative && <title>{title}</title>}
      {/* draw order: left → center → right (right on top) */}
      <path d={PATHS.left} fill={recipe.left} />
      <path d={PATHS.center} fill={recipe.center} />
      <path d={PATHS.right} fill={recipe.right} />
    </svg>
  )
}

// ── Wordmark ────────────────────────────────────────────────────

const WORDMARK_FONT = "'Bricolage Grotesque', ui-sans-serif, system-ui, -apple-system, sans-serif"

interface WordmarkRecipe {
  prefix: string
  suffix: string
}

const WORDMARK_RECIPES: Record<LogoSurface, WordmarkRecipe> = {
  paper: { prefix: COLORS.ink, suffix: COLORS.terra },
  felt: { prefix: COLORS.paper, suffix: COLORS.terraLight },
  ink: { prefix: COLORS.paper, suffix: COLORS.terraLight },
  terra: { prefix: COLORS.paper, suffix: COLORS.giltLight },
}

export interface WordmarkProps {
  /** Visual size — the height of the lowercase letters in pixels. Drives font-size, mark size, gap. Default: 40 */
  size?: number
  surface?: LogoSurface
  layout?: 'horizontal' | 'stacked'
  /** Suppress the mark and render the wordmark alone. Default: true (mark shown) */
  showMark?: boolean
  tagline?: string
  className?: string
  style?: CSSProperties
}

/**
 * Full topmeeps lockup — mark + wordmark. Type: Bricolage Grotesque 700,
 * letter-spacing -0.5 at default sizes, tightening proportionally larger.
 */
export function Wordmark({
  size = 40,
  surface = 'paper',
  layout = 'horizontal',
  showMark = true,
  tagline,
  className,
  style,
}: WordmarkProps) {
  const recipe = WORDMARK_RECIPES[surface]
  const markSize = layout === 'horizontal' ? Math.round(size * 1.55) : Math.round(size * 1.85)
  const fontSize = size
  const tracking = -size * 0.0125 // -0.5 at 40px, scales proportionally
  const horizGap = Math.round(size * 0.28)
  const stackGap = Math.round(size * 0.28)

  const wordmark = (
    <span
      style={{
        fontFamily: WORDMARK_FONT,
        fontWeight: 700,
        fontSize,
        letterSpacing: tracking,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'baseline',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: recipe.prefix }}>top</span>
      <span style={{ color: recipe.suffix }}>meeps</span>
    </span>
  )

  const taglineEl = tagline ? (
    <span
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: Math.max(10, Math.round(size * 0.26)),
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        color: surface === 'paper' ? '#8A7559' : 'rgba(242,235,218,0.7)',
        marginTop: Math.round(size * 0.18),
      }}
    >
      {tagline}
    </span>
  ) : null

  if (layout === 'stacked') {
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: stackGap,
          ...style,
        }}
      >
        {showMark && <LogoMark size={markSize} surface={surface} title="" />}
        {wordmark}
        {taglineEl}
      </span>
    )
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: horizGap,
        ...style,
      }}
    >
      {showMark && <LogoMark size={markSize} surface={surface} title="" />}
      {tagline ? (
        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {wordmark}
          {taglineEl}
        </span>
      ) : (
        wordmark
      )}
    </span>
  )
}

export default LogoMark
